import { useCallback, useEffect, useState } from "react";
import type { Agent } from "./fixtures";
import { useAgentsList, useNotifications, useTemplates } from "./useAgentsUi";
import { NewAgentDialog } from "./new-agent-dialog";
import { RunningNow } from "./RunningNow";
import { RoutineCard } from "./RoutineCard";
import { ActivityView } from "./views";
import { NotificationsFeed, unresolvedCount } from "./notifications";
import { useChatAgentScope } from "@/app/providers/ChatAgentScopeContext";
import { conversationEvents } from "@/lib/conversation-events";

type ModuleView = "agents" | "notifications" | "activity";

/**
 * Agents module shell — the post-pivot "window": the module OBSERVES (runs + routines),
 * chat AUTHORS. One nav layer (three tabs); the Agents tab stacks RUNNING NOW (live runs)
 * over ROUTINES (the agent cards, which expand inline). No chip-strip, no lens tabs.
 * Clicking a routine card expands it AND scopes the right-rail chat to that agent.
 */
export function AgentsHome() {
  const { data: agents = [], isLoading } = useAgentsList();
  const { data: notifs = [] } = useNotifications();
  const { scope, setScope, clearScope } = useChatAgentScope();
  const [view, setView] = useState<ModuleView>("agents");
  // The inline-expanded routine (accordion, single-open). null = all collapsed.
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [showNew, setShowNew] = useState(false);
  // When set, the Activity tab opens pre-filtered to this agent ("Open in Activity →").
  const [activityAgentId, setActivityAgentId] = useState<string | undefined>(
    undefined,
  );

  const pendingTotal = agents.reduce((n, a) => n + a.pendingCount, 0);
  const unresolvedNotifs = unresolvedCount(notifs);

  // Scope the right-rail chat to an agent — its identity becomes the "talking to ●" chip
  // (Phase 4, piece 3; visual scoping only — message routing is deferred/live work).
  const scopeToAgent = useCallback(
    (agent: Agent) => {
      setScope({
        agentId: agent.id,
        agent: { name: agent.name, color: agent.color, status: agent.status },
      });
    },
    [setScope],
  );

  // Click a routine card: expand it (collapse any other) and steer chat to it; clicking the
  // open card collapses it and clears the scope back to the orchestrator.
  const toggleRoutine = useCallback(
    (agent: Agent) => {
      setExpandedId((cur) => {
        if (cur === agent.id) {
          clearScope();
          return null;
        }
        scopeToAgent(agent);
        return agent.id;
      });
    },
    [clearScope, scopeToAgent],
  );

  // "Refine by talking in chat": re-affirm the scope and reveal the main chat (the canonical
  // open-chat gesture). The card is already scoped from the expand.
  const refineInChat = useCallback(
    (agent: Agent) => {
      scopeToAgent(agent);
      window.dispatchEvent(new CustomEvent(conversationEvents.closeModulePane));
    },
    [scopeToAgent],
  );

  // "Answer in chat" on a notification: scope the chat to the run and reveal chat.
  const steerToRun = useCallback(
    (agentId: string, runId: string) => {
      const agent = agents.find((a) => a.id === agentId);
      if (agent) {
        setScope({
          agentId,
          runId,
          agent: { name: agent.name, color: agent.color, status: agent.status },
        });
      }
      window.dispatchEvent(new CustomEvent(conversationEvents.closeModulePane));
    },
    [agents, setScope],
  );

  // Keep the expanded routine in sync with the chat scope: clearing the scope from the chat
  // (the × on the scope chip) collapses the card back to its compact look — and dropping the
  // highlighted border. One source of truth: no scope ⇒ no expanded card.
  useEffect(() => {
    if (!scope && expandedId) setExpandedId(null);
  }, [scope, expandedId]);

  // Click a "Running now" row: open the SAME consolidated detail a routine card opens —
  // expand that agent's card (collapsing any other) and steer chat to it. Matches the
  // routine-card open path so there are no dead tiles.
  const openAgentCard = useCallback(
    (agentId: string) => {
      const agent = agents.find((a) => a.id === agentId);
      if (!agent) return;
      setView("agents");
      setExpandedId(agentId);
      scopeToAgent(agent);
    },
    [agents, scopeToAgent],
  );

  // "Open in Activity →" from a routine: jump to Activity pre-filtered to that agent.
  const openActivity = useCallback((agentId: string) => {
    setActivityAgentId(agentId);
    setView("activity");
  }, []);

  // Tab navigation: switching to Activity via the tab shows the full history (no preset).
  const goToView = useCallback((v: ModuleView) => {
    if (v === "activity") setActivityAgentId(undefined);
    setView(v);
  }, []);

  const handleCreated = (id: string) => {
    setShowNew(false);
    setView("agents");
    setExpandedId(id);
  };

  return (
    <div className="agui">
      <ModuleViewTabs
        view={view}
        setView={goToView}
        pendingTotal={pendingTotal}
        unresolvedNotifs={unresolvedNotifs}
      />

      {view === "agents" ? (
        <>
          <RunningNow onOpen={openAgentCard} />
          <RoutinesSection
            agents={agents}
            isLoading={isLoading}
            expandedId={expandedId}
            onToggle={toggleRoutine}
            onRefineInChat={refineInChat}
            onOpenActivity={openActivity}
            onClearExpanded={() => {
              setExpandedId(null);
              clearScope();
            }}
            onNewAgent={() => setShowNew(true)}
          />
        </>
      ) : view === "notifications" ? (
        // Approvals folded in: sign-offs are rows that expand into the inline sign-off card.
        <NotificationsFeed onSteer={steerToRun} />
      ) : (
        <ActivityView initialAgentId={activityAgentId} />
      )}

      {showNew ? (
        <NewAgentDialog
          onClose={() => setShowNew(false)}
          onCreated={handleCreated}
        />
      ) : null}
    </div>
  );
}

function ModuleViewTabs({
  view,
  setView,
  pendingTotal,
  unresolvedNotifs,
}: {
  view: ModuleView;
  setView: (v: ModuleView) => void;
  pendingTotal: number;
  unresolvedNotifs: number;
}) {
  // Approvals folded into Notifications → its badge counts pending sign-offs too.
  const tabs: Array<{ id: ModuleView; label: string; badge?: number }> = [
    { id: "agents", label: "Agents" },
    {
      id: "notifications",
      label: "Notifications",
      badge: unresolvedNotifs + pendingTotal || undefined,
    },
    { id: "activity", label: "Activity" },
  ];
  return (
    <nav className="agui-viewtabs" aria-label="Agents module views">
      {tabs.map((t) => (
        <button
          key={t.id}
          type="button"
          className={`agui-viewtab${view === t.id ? " is-active" : ""}`}
          aria-current={view === t.id ? "page" : undefined}
          onClick={() => setView(t.id)}
        >
          {t.label}
          {t.badge ? (
            <span className="agui-viewtab__badge">{t.badge}</span>
          ) : null}
        </button>
      ))}
    </nav>
  );
}

function RoutinesSection({
  agents,
  isLoading,
  expandedId,
  onToggle,
  onRefineInChat,
  onOpenActivity,
  onClearExpanded,
  onNewAgent,
}: {
  agents: Agent[];
  isLoading: boolean;
  expandedId: string | null;
  onToggle: (agent: Agent) => void;
  onRefineInChat: (agent: Agent) => void;
  onOpenActivity: (agentId: string) => void;
  onClearExpanded: () => void;
  onNewAgent: () => void;
}) {
  const [showTemplates, setShowTemplates] = useState(false);
  if (isLoading) return <div className="agui-stub">Loading agents…</div>;

  return (
    <section className="agui-routines">
      <div className="agui-roster__header">
        <span className="agui-block__label">Routines</span>
        <div className="agui-roster__headlinks">
          <button
            type="button"
            className="agui-link"
            onClick={() => setShowTemplates((s) => !s)}
          >
            {showTemplates ? "Hide templates" : "Templates"}
          </button>
          <button type="button" className="agui-link" onClick={onNewAgent}>
            + New
          </button>
        </div>
      </div>

      {showTemplates ? <TemplatesStrip /> : null}

      <div
        className={`agui-roster${expandedId ? " has-expanded" : ""}`}
      >
        {agents.map((a) => (
          <div
            key={a.id}
            className={`agui-roster__slot${expandedId === a.id ? " is-expanded" : ""}`}
          >
            <RoutineCard
              agent={a}
              expanded={expandedId === a.id}
              onToggle={() => onToggle(a)}
              onRefineInChat={() => onRefineInChat(a)}
              onOpenActivity={() => onOpenActivity(a.id)}
              onDeleted={onClearExpanded}
            />
          </div>
        ))}
      </div>
    </section>
  );
}

function TemplatesStrip() {
  const { data: templates = [] } = useTemplates();
  return (
    <div className="agui-templates">
      {templates.map((t) => (
        <button key={t.id} type="button" className="agui-template">
          <span className="agui-template__name">{t.name}</span>
          <span className="agui-template__blurb">{t.blurb}</span>
        </button>
      ))}
    </div>
  );
}
